import { Button, Dialog, DialogContent, DialogTitle } from "@mui/material";
import { ChoiceCard } from "portal/components/categoryCollectionProfile/ChoiceCard";
import { CreateCategoryDialogActions } from "portal/components/categoryCollectionProfile/createCategory/CreateCategoryDialogActions";
import { ExistingCategoryTemplateStep } from "./existing/ExistingCategoryTemplateStep";
import {
  getCategoryTemplateEditPath,
  getCategoryTemplatesPath,
} from "portal/utils/routing";
import { NewCategoryTemplateStep } from "./new/NewCategoryTemplateStep";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { withCategoryTemplatesUpdateAccess } from "portal/views/categoryTemplates/permissions";
import AddIcon from "@mui/icons-material/AddOutlined";
import PlantProfilesIcon from "@mui/icons-material/YardOutlined";
import React, { FC, useState } from "react";

const STEP_CHOOSE = "choose";
const STEP_EXISTING = "existing";
const STEP_NEW = "new";

type AddCategoryTemplateStep =
  | typeof STEP_CHOOSE
  | typeof STEP_EXISTING
  | typeof STEP_NEW;

interface AddCategoryTemplateProps {
  customerUuid?: string;
}

const _AddCategoryTemplate: FC<AddCategoryTemplateProps> = ({
  customerUuid,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isOpen, setOpen] = useState(false);
  const [step, setStep] = useState<AddCategoryTemplateStep>(STEP_CHOOSE);

  const handleOpen = (): void => {
    setStep(STEP_CHOOSE);
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  const handleExistingSubmitSuccess = (newCategoryId: string): void => {
    handleClose();
    navigate(getCategoryTemplatesPath(newCategoryId));
  };

  const handleNewSubmitSuccess = (newCategoryId: string): void => {
    handleClose();
    navigate(getCategoryTemplateEditPath(newCategoryId));
  };

  return (
    <>
      <Button
        color="inherit"
        startIcon={<AddIcon />}
        className="leading-[0]"
        onClick={handleOpen}
      >
        {t("components.categoryTemplates.actions.addCategory")}
      </Button>
      <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>
          {t("components.categoryTemplates.actions.addCategory")}
        </DialogTitle>
        {isOpen && (
          <DialogContent>
            {step === STEP_CHOOSE && (
              <>
                <div className="flex flex-col gap-3">
                  <ChoiceCard
                    icon={<PlantProfilesIcon fontSize="small" />}
                    label={t(
                      "components.categoryTemplates.actions.addExisting"
                    )}
                    description={t(
                      "components.categoryTemplates.actions.addExistingDetail"
                    )}
                    onClick={() => setStep(STEP_EXISTING)}
                  />
                  <ChoiceCard
                    icon={<AddIcon fontSize="small" />}
                    label={t(
                      "components.categoryCollectionProfile.actions.createNewCategory"
                    )}
                    description={t(
                      "components.categoryCollectionProfile.actions.createNewCategoryDetail"
                    )}
                    onClick={() => setStep(STEP_NEW)}
                  />
                </div>
                <CreateCategoryDialogActions onCancel={handleClose} />
              </>
            )}
            {step === STEP_EXISTING && (
              <ExistingCategoryTemplateStep
                customerUuid={customerUuid}
                onBack={() => setStep(STEP_CHOOSE)}
                onCancel={handleClose}
                onSubmitSuccess={handleExistingSubmitSuccess}
              />
            )}
            {step === STEP_NEW && (
              <NewCategoryTemplateStep
                customerUuid={customerUuid}
                onBack={() => setStep(STEP_CHOOSE)}
                onCancel={handleClose}
                onSubmitSuccess={handleNewSubmitSuccess}
              />
            )}
          </DialogContent>
        )}
      </Dialog>
    </>
  );
};

export const AddCategoryTemplate =
  withCategoryTemplatesUpdateAccess(_AddCategoryTemplate);
